

import { Component, Emit, Prop, Vue } from "vue-property-decorator"
import {
  class_activity_forced_kick_topic_prefix,
  class_activity_status_topic_prefix,
  teacher_online_status_topic_prefix
} from "@/ts/Constant"
import MqttClient from "@/mqtt/mqttClient"
import RTCClient from "@/rtc/RTCClient";

@Component({})
export default class ClassActivityJoinComponent extends Vue {

  /**
   * mqtt客户端ID
   */
  @Prop({
    type: String,
    default: () => {
      return ''
    },
    required: true
  })
  mqttClientId
  /**
   * 要订阅班级活动状态topic的班级列表
   */
  @Prop({
    type: Array,
    default: () => {
      return []
    },
    required: true,
  })
  classList
  /**
   * 园区ID
   */
  @Prop({
    type: String,
    default: () => {
      return ''
    },
    required: true
  })
  gardenId

  /**
   * 用户认证TOKEN
   */
  @Prop({
    type: String,
    default: () => {
      return ''
    },
    required: true
  })
  token

  @Prop({
    type: String,
    default: null,
    required: true
  })
  userType

  loading = true
  // 班级教学活动状态消息主题
  classActivityStatusTopic = {
    topic: `${class_activity_status_topic_prefix}{classId}`,
    qos: 2,
    pattern: `^${class_activity_status_topic_prefix}{classId}$`,
  }
  // 教师在线状态消息主题
  teacherOnlineStatusTopic = {
    topic: `${teacher_online_status_topic_prefix}{teacherId}`,
    qos: 2,
    pattern: `^${teacher_online_status_topic_prefix}{teacherId}$`,
  }
  // 班级活动互踢消息主题
  classActivityForcedKickTopic = {
    topic: `${class_activity_forced_kick_topic_prefix}{classId}`,
    qos: 2,
    pattern: `^${class_activity_forced_kick_topic_prefix}{classId}$`,
  }
  // 在线教师Id的set集合
  onlineTeacherIdSet = new Set()
  // 所有接收到的活动的集合
  activityList = []
  // 当前客户端是否支持rtc，默认为false
  currentClientSupportRtc = false

  get activeActivityList() {
    return this.activityList.filter(item => {
      return item.activity_teacher_online_status
    })
  }

  created(): void {
    this.checkRtcSupport()
  }

  mounted(): void {
    this.connectMqtt()
  }

  /**
   * 连接MQTT服务器
   */
  connectMqtt() {
    MqttClient.instance.end().then(() => {
      MqttClient.instance.setClientId(this.mqttClientId)
      MqttClient.instance.connect()
      //注册mqtt 回调事件
      this.registerCallBackMqtt()
    })
  }

  /**
   * 注册mqtt 回调事件
   */
  registerCallBackMqtt() {
    MqttClient.instance.registerCallBack((eventName, data) => {
      switch (eventName) {
        case 'connect':
          this.subscribeClassActivityMessage()
          setTimeout(() => {
            this.loading = false
          }, 2000)
          break;
        case 'message':
          this.processMessage(data)
          break;
        case 'close':
          this.mqttClose(data)
      }
    })
  }

  /**
   *  订阅班级下的马良笔双师课堂的topic
   */
  subscribeClassActivityMessage() {
    if (!this.classList || this.classList.length <= 0) {
      return
    }
    // 循环订阅所属班级的马良笔双师课堂活动主题
    this.classList.forEach((item) => {
      // 订阅班级活动主题
      const topicParams = {
        topic: `${class_activity_status_topic_prefix}${item.classId}`,
        qos: 2
      }
      this.doSubscribe(topicParams)
      // 订阅班级互踢主题
      const forcedKickTopic = {
        topic: `${class_activity_forced_kick_topic_prefix}${item.classId}`,
        qos: 2
      }
      this.doSubscribe(forcedKickTopic)
    })
  }

  /**
   *  订阅主题
   * @param subScription
   */
  async doSubscribe(subScription) {
    MqttClient.instance.doSubscribe(subScription);
  }

  /**
   * 取消订阅主题
   */
  doUnSubscribe(topic) {
    MqttClient.instance.doUnSubscribe({ topic: topic });
  }

  /**
   * mqtt 处理消息
   */
  processMessage(data) {
    // console.log(`data: ${data.topic},=>${data.message}`);

    const activityStatusTopicPattern = new RegExp(`^${class_activity_status_topic_prefix}[0-9]*$`, 'g');
    if (activityStatusTopicPattern.test(data.topic)) {
      this.processActivityStatusMessage(data.message)
      return
    }

    const teacherOnlineStatusTopicPattern = new RegExp(`^${teacher_online_status_topic_prefix}[0-9]*$`, 'g')
    if (teacherOnlineStatusTopicPattern.test(data.topic)) {
      this.processTeacherOnlineStatusMessage(data.message)
    }

    const classActivityForcedKickTopicPattern = new RegExp(`^${class_activity_forced_kick_topic_prefix}[0-9]*$`, 'g');
    if (classActivityForcedKickTopicPattern.test(data.topic)) {
      this.processForcedKickMessage(data.message)
    }
  }

  /**
   * 处理活动状态相关的消息内容
   *
   * <p>
   *   1、如果当前收到的消息为空，或者活动类型不是马良笔双师课堂，则返回不做处理
   *   2、当前是马良笔双师课堂的消息，且是活动开始消息。
   *    先订阅该条消息的教师在线状态主题；
   *    再将该条消息的内容保存到全局变量的等待发起的活动信息内。
   *    等待收到该教师的在线消息后，再将该活动展示到列表中。
   *  3、当前是马良笔双师课堂的消息，并且是活动结束消息。
   *    取消订阅该条消息对应的教师的在线状态主题。
   *    移除展示列表中该消息对应的展示列。
   * </p>
   */
  processActivityStatusMessage(message) {
    // 当前topic没有消息或者是消息中的活动类型不是 马良笔双师课堂，不做任何处理
    if (
      !message ||
      !message.activity_type ||
      message.activity_type !== "RTC_INTERACTIVE_CLASS"
    ) {
      return;
    }
    let activityInfo = message
    activityInfo.activity_teacher_id = message.teacher_id
    activityInfo.activity_teacher_name = message.teacher_name
    activityInfo.activity_teacher_online_status = false
    activityInfo.received_time = new Date()

    // 判断当前可以参与的活动列表中，有没有同班级同教师发起的马良笔双师课堂活动
    // 如果有同班级同教师发起的马良笔双师课堂活动，那就将其从活动列表中移除。
    // 一个班级一个教师，同时只保留一个最新的活动信息即可
    if (this.activityList) {
      let index = 0
      while (index != -1) {
        index = this.activityList.findIndex(item =>
          item.activity_teacher_id == activityInfo.activity_teacher_id &&
          item.class_id == activityInfo.class_id
        )
        if (index > -1) {
          this.activityList.splice(index, 1)
        }
      }
    }

    if (
      activityInfo.activity_instruction === "START_ACTIVITY" &&
      activityInfo.instruction_order === 0
    ) {
      // 收到新的活动之后。 如果当前活动对应的教师在线，则更改活动的教师在线状态，将活动添加到当前的活动列表集合中
      if (this.onlineTeacherIdSet && this.onlineTeacherIdSet.has(activityInfo.activity_teacher_id)) {
        activityInfo.activity_teacher_online_status = true
        this.activityList.push(activityInfo)
        this.loading = false
      } else {
        // 如果当前活动的发起教师不在线，先将活动添加到当前活动的列表集合中，等待教师上线后，在从列表中找到该活动，更改活动的教师在线状态
        this.activityList.push(activityInfo)
        // 订阅当前活动发起教师的在线状态，等待教师在线后再进入活动
        const teacherOnlineStatusTopic = `${teacher_online_status_topic_prefix}${activityInfo.activity_teacher_id}`
        this.doSubscribe({ topic: teacherOnlineStatusTopic, qos: 2 })
      }

    } else if (
      activityInfo.activity_instruction === "END_ACTIVITY"
    ) {
      // 收到活动结束的消息之后，从当前可以进入的活动列表中，将与当前消息内活动Id一直的内容移除
      const index = this.activityList ? this.activityList.findIndex(e => e.activity_id === activityInfo.activity_id) : -1
      if (index > -1) {
        this.activityList.splice(index, 1)
      }
      // 从在线教师Set集合中移除当前活动对应的教师（教师对应的活动结束后，该教师是否在线已经无意义了。
      // 下次教师发起活动时，会重新订阅该教师的在线状态主题，等待其重新上线后才会进入新的活动。）
      this.onlineTeacherIdSet.delete(activityInfo.activity_teacher_id)
      // 取消订阅活动结束的教师的在线状态topic
      const teacherOnlineStatusTopic = `${this.teacherOnlineStatusTopic.topic}${activityInfo.activity_teacher_id}`
      this.doUnSubscribe(teacherOnlineStatusTopic)
    }
  }

  /**
   * 教师在线状态消息处理
   *
   * <p>
   *  教师在线：
   *    当前活动列表中，发起教师是当前在线的教师Id的活动，更改其教师在线状态为true
   *  教师离线：
   *    前活动列表中，移除发起教师是当前在线的教师Id的活动
   * </p>
   */
  processTeacherOnlineStatusMessage(message) {
    if (!message) {
      return
    }
    // 如果教师状态下线
    if (message.online_status === 'offline') {
      // 移除在线教师Id集合中的该教师Id
      this.onlineTeacherIdSet.delete(message.id)
      // 从当前的活动集合中，找到发起教师Id是该离线教师Id的活动，将其教师在线状态更改为false。
      // 只是更改状态，不要移除该条数据。避免收到活动开始消息，订阅教师在线状态消息后，先收到的是教师离线的消息，后又收到该教师上线，导致活动无法正常展示。
      if (this.activityList) {
        this.activityList.forEach(item => {
          if (item.activity_teacher_id == message.id) {
            item.activity_teacher_online_status = false
          }
        })
      }
      return
    }

    // 如果教师是上线
    if (message.online_status === 'online') {
      // 从当前的活动集合中，找到发起教师是该教师Id的活动，更改其教师在线状态为true
      if (this.activityList) {
        this.activityList.forEach(item => {
          if (item.activity_teacher_id == message.id) {
            item.activity_teacher_online_status = true
          }
        })
        this.loading = false
      }
    }
  }

  /**
   * 处理活动被强踢的消息
   *
   * 收到被强提消息后，如果被踢的活动类型是马良笔双师课堂活动，就从当前的活动集合中，找到与该活动Id一致的活动，将其移除。
   *
   */
  processForcedKickMessage(message) {
    if (
      !message ||
      !message.kicked_activity ||
      message.kicked_activity.activity_type !== 'RTC_INTERACTIVE_CLASS'
    ) {
      return
    }

    const index = this.activityList.findIndex(item => item.activity_id == message.kicked_activity.activity_id)
    if (index > -1) {
      this.activityList.slice(index, 1)
    }
  }

  /**
   * mqtt客户端连接关闭回调函数
   *
   * 无论是因为异常原因断开还是主动断开，都会走此回调。
   * 主动退出时，不做任何处理。
   * 如果不是主动退出，不论任何原因，提示用户已经断开，询问用户是否重连。
   *
   * @param initiativeQuit 是否是主动退出。主动退出为true，否则为false
   *
   */
  mqttClose(initiativeQuit) {
    // mqtt连接关闭时，将当前可以参与的活动一起移除掉，避免mqtt重新连接后，收到相同的活动开始消息时将相同的活动信息展示在页面上。
    this.activityList = []
    if (initiativeQuit) {
      return
    }
    let _that = this
    const confirmHtml = `您当前和服务端的消息服务器断开连接！造成断开连接的原因可能包括以下两种：<br/>
1.您的账号在其他地方登录并使用了连接消息服务器服务造成将您当前的连接断开；<br/>
2.由于网络连接不通导致您和消息服务器断开。`
    this.$alert(confirmHtml, "断开连接提示", {
      confirmButtonText: "尝试重新连接",
      cancelButtonText: '退出马良笔双师课堂 ',
      dangerouslyUseHTMLString: true,
      showCancelButton: true,
      closeOnClickModal: false,
      showClose: false,
    }).then(() => {
      MqttClient.instance.reconnect()
    }).catch((e) => {
      console.log(e)
      MqttClient.instance.end().then(() => {
        _that.exitFusionClassroom()
      })
    })
  }

  /**
   * 校验浏览器是否支持rtc
   */
  checkRtcSupport() {
    RTCClient.instance.isSupport().then(res => {
      const {isSupported} = res
      // 如果不支持，给出提示弹框
      this.currentClientSupportRtc = isSupported
      RTCClient.instance.dispose()
    }, err => {
      this.currentClientSupportRtc = false
      RTCClient.instance.dispose()
    }).catch(err => {
      this.currentClientSupportRtc = false
      RTCClient.instance.dispose()
    })
  }

  /**
   * 展示当前浏览器不支持rtc时的确认弹框
   */
  showRtcNotSupportConfirm() {
    let confirmTitle =
      '<div style="font-size: 16px;text-indent:2em;line-height: 30px">您当前使用的浏览器类型或版本不支持发起音视频双向通信。请更换至以下浏览器类型或版本后再试：\n' +
      'Chrome浏览器72或以上版本，Edge浏览器80或以上版本</div>';

    this.$elementConfirm(confirmTitle, "提示", {
      confirmButtonText: "确定",
      dangerouslyUseHTMLString: true,
      showCancelButton: false,
      closeOnClickModal: false,
      transition: "",
      // type: 'warning'
    }).then(() => {
    })
  }

  /**
   * 进入课堂按钮点击事件
   * <p>
   *  跳转到马良笔双师课堂
   * </p>
   */
  joinClassroom(item) {
    if (!this.currentClientSupportRtc) {
      this.showRtcNotSupportConfirm()
      return
    }

    RTCClient.instance.getAuthInfo(
      this.token,
      item.channel_id,
      'PA',
      this.userType,
      this.gardenId
    ).then(res => {
      this.$emit('joinClassroom', item, res)
    }).catch(err => {
      switch (err.Code) {
        case 700:
        case 702:
        case 703:
          //  701:云端未知异常
          //  702:云端获取园区对应的rtc AppID和appkey 报错
          //  703:云端获取当前频道ID的可用性时报错
          this.$message(err.Message.UserMsg)
          break
        case 701:
          //  用户的优课场域认证token时效，回到登录页面
          this.$message.error('您当前的音视频通信认证令牌已超过最大允许时长。系统将自动结束当前课堂。')
          this.local$.clearLocal()
          this.cookie$.removeCookie("LWToken")
          window.open(this.filePre$.loginUrl + "/logout", "_self")
          break
        case 704:
          //  当前请求的频道ID不可用
          this.$message('根据当前课堂ID码未查询到当前正在进行的课堂')
          break
        default:
          this.$message(err.Message.UserMsg)
          break
      }
    })
  }

  /**
   * 退出马良笔双师课堂
   */
  @Emit()
  exitFusionClassroom(): void {

  }
}
